import { Input, Modal, message } from '@duxton-web/ui';
import i18n from 'i18n-js';
import _ from 'lodash';
import {
  CONTENT_GENERATION_CONFIG_GET_LATEST,
  contentConfigGenerateMystiqueV2,
  contentGenerationConfigGetLatest,
  cpApprove, cpCreateProduct, cpGetCountries, cpGetPersonas, cpGetVerticals, cpListAttributes,
  cpListAudiences, cpListCvps, cpListProducts, cpRecall, cpReject, cpUpdateProduct,
} from 'omega-rtc/actions/ucd';
import {
  ContentConfigStatus,
} from 'omega-rtc/types/ts/content-config';
import type {
  AudienceReference,
  FunnelObjective,
  ProductAudienceAssociation,
  ProductAudienceOption,
} from 'omega-rtc/types/ts/content-config';
import {
  isHolosPortalFeatureEnabled,
  useContentGenerationEndpointMigrationEnabled,
  useHolosPortalFeatureFlag,
} from 'omega-rtc/utils/portal-config';
import { getRequestStateKey } from 'omega-rtc/utils/ucd-utils';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';

import {
  STATUS_PENDING, audienceDisplayLabel, audienceReferenceFromApi,
  buildProductAudienceOptions, countryFromApi, cvpFromApi, formatExpiry,
  isActive, isAudienceLangValid, isAudienceReasonValid, isProductDeeplinkValid, localeSorter,
  personaFromApi, productAttributeOptionsFromApi, productDisplayName, productFromApi,
  productToApiBody, resolveGenerationObjectives, sameAudienceAssociations,
  sameFunnelObjectives, verticalFromApi,
} from './adapters';
import type { GenerationFunnelObjective } from './adapters';
import { EditChangesMode, EditChangesSeed, editChangesText } from './editChanges';
import {
  NewProductModal, ProductOfferingDetail, ProductOfferingList,
} from './ProductOfferingView';
import ContentGenerationModal from './ContentGenerationModal';
import ContentGenerationSetupModal, {
  ContentGenerationSetupAudienceOption,
  ContentGenerationSetupObjectiveOption,
} from './ContentGenerationSetupModal';
import {
  buildContentConfigGenerationRequests,
  buildGenerationProductConfig,
  resolveGenerationLanguageConfig,
  selectMissingContentConfigGenerationRequests,
} from './ConfigContentGeneration.helpers';
import type {
  GenerationConfigSource,
  GenerationProductConfig,
} from './ConfigContentGeneration.helpers';
import {
  EMPTY_CONTENT_CONFIG_PERMISSIONS,
  canRecallPendingChange,
  canReviewPendingChange,
  canSubmitProductForApproval,
} from './permissions';
import useContentConfigWrite from './useContentConfigWrite';

export type { GenerationConfigSource };

const DRAFT_KEY = '__draft__';
const FORBIDDEN_MESSAGE = "You don't have permission to perform this action.";
const EMPTY_ARRAY: any[] = [];
const EMPTY_STRING_ARRAY: string[] = [];
const EMPTY_GENERATION_REQUESTS: any[] = [];
const EMPTY_GENERATION_OBJECTIVES: GenerationFunnelObjective[] = [];
const EMPTY_OBJECT = {};
const EMPTY_GENERATION_RUN = {
  audienceKeys: EMPTY_STRING_ARRAY,
  audienceLabelsByKey: {},
  audienceWhitelistByKey: {},
  configSource: 'active' as GenerationConfigSource,
  context: {
    country: '', endDate: '', language: '', objectiveID: '', objectiveName: '', product: '', startDate: '', vertical: '',
  },
  requests: EMPTY_GENERATION_REQUESTS,
  selectionKey: '',
};

export const isProductOfferingAudiencesEnabled = () =>
  isHolosPortalFeatureEnabled('enabledProductOfferingAudiences');

export const isProductOfferingObjectivesEnabled = () =>
  isHolosPortalFeatureEnabled('enabledProductOfferingObjectives');

export const getProductAudienceValidationState = (
  audiencesEnabled: boolean,
  audienceListLoaded: boolean,
  associations: ProductAudienceAssociation[],
  optionByKey: Map<string, ProductAudienceOption>,
) => {
  const hasAssociations = audiencesEnabled && associations.length > 0;
  const audienceValidationUnavailable = hasAssociations && !audienceListLoaded;
  const hasInvalidReference = hasAssociations
    && audienceListLoaded
    && associations.some(({ audienceKey }) => {
      const option = optionByKey.get(audienceKey);
      return !option || !option.eligible;
    });

  return {
    audienceValidationUnavailable,
    hasInvalidAudiences: audienceValidationUnavailable || hasInvalidReference,
  };
};

// True when a configured association names a language the Product Offering's
// country does not configure. An empty `countryLangs` means the country config
// has not loaded, or the country configures no languages at all — in both cases
// the product editor already has no language tabs and UCD rejects any
// association-bearing write, so do not claim "invalid" here.
export const hasInvalidProductAudienceLangs = (
  audiencesEnabled: boolean,
  countryLangs: string[],
  associations: ProductAudienceAssociation[],
): boolean => audiencesEnabled
  && countryLangs.length > 0
  && associations.some(({ lang }) => !isAudienceLangValid(lang, countryLangs));

export const buildGenerationAudienceOptions = (
  audienceReferences: AudienceReference[],
  country: string,
  countryName: (country: string) => string,
  whitelistedAudienceKeys: string[],
): ContentGenerationSetupAudienceOption[] => {
  const whitelistedAudienceKeySet = new Set(whitelistedAudienceKeys);
  const preferredAudienceByKey = new Map<string, AudienceReference>();

  audienceReferences
    .filter((audience) => !audience.country || audience.country === country)
    .forEach((audience) => {
      if (!audience.key) return;

      const currentAudience = preferredAudienceByKey.get(audience.key);
      const currentIsCountrySpecific = Boolean(currentAudience && currentAudience.country === country);
      const candidateIsCountrySpecific = audience.country === country;

      if (!currentAudience || (candidateIsCountrySpecific && !currentIsCountrySpecific)) {
        preferredAudienceByKey.set(audience.key, audience);
      }
    });

  return Array.from(preferredAudienceByKey.values()).map((audience) => ({
    key: audience.key,
    label: audienceDisplayLabel(audience, countryName),
    whitelisted: whitelistedAudienceKeySet.has(audience.key),
  })).sort((left, right) => {
    if (left.whitelisted !== right.whitelisted) {
      return left.whitelisted ? -1 : 1;
    }
    return left.label.localeCompare(right.label);
  });
};

export const buildGenerationObjectiveOptions = (
  objectives: GenerationFunnelObjective[] = [],
): ContentGenerationSetupObjectiveOption[] => {
  const optionsByID = new Map<string, ContentGenerationSetupObjectiveOption>();

  objectives.forEach((objective) => {
    // generationID, not id: an objective that exists only on an unsaved DRAFT
    // or in local edits has no UCD id yet and must still be selectable.
    const id = String(objective.generationID || '').trim();
    if (!id || optionsByID.has(id)) return;
    const name = String(objective.name || '').trim();
    const description = String(objective.description || '').trim();
    const label = name
      ? `${name}${description ? ` — ${description}` : ''}`
      : id;
    optionsByID.set(id, { id, label });
  });

  return Array.from(optionsByID.values())
    .sort((left, right) => left.label.localeCompare(right.label));
};

export const getGenerationObjectives = (
  objectivesEnabled: boolean,
  objectives: FunnelObjective[] = [],
): GenerationFunnelObjective[] => (objectivesEnabled
  ? resolveGenerationObjectives(objectives)
  : EMPTY_GENERATION_OBJECTIVES);

export interface ContentGenerationState {
  configSource: GenerationConfigSource;
  disabledReason: string;
}

// Which record the edit sandbox belongs to. Derived from the record actually
// on screen, not from showPending: a never-approved offering displays its
// pending version while showPending stays false, because the "See changes"
// toggle only renders once an active version exists.
export const getEditVersionKey = (
  isDraft: boolean,
  base: any,
  sel: any,
): 'draft' | 'pending' | 'active' => {
  if (isDraft) return 'draft';
  return sel && base && base === sel.pending ? 'pending' : 'active';
};

const PRODUCT_LANG_FIELDS = ['name', 'ciEnabled', 'overview', 'reason', 'cta'];
const PRODUCT_LIST_FIELDS = ['cvps', 'attrs', 'personas', 'audiences', 'objectives'];

export interface ProductFE {
  byLang: Record<string, any>;
  deeplink: string;
  name: string;
  vertical: string;
  whitelist: Record<string, any[]>;
}

// Value resolution of an edit sandbox over a base record — the same rules the
// detail view uses to render and dirty-check the form.
const productValues = (base: any, edits: any, langs: string[], fallbackVertical: string) => {
  const baseLang = (lang, field) => {
    const b = base && base.byLang && base.byLang[lang];
    return b && b[field] !== undefined ? b[field] : '';
  };
  const lang = (key, field) => {
    const e = edits && edits.byLang && edits.byLang[key];
    return e && e[field] !== undefined ? e[field] : baseLang(key, field);
  };
  const baseName = () => (base && base.name) || baseLang(langs[0] || 'en', 'name');
  return {
    deeplink: () => (edits && edits.deeplink !== undefined
      ? edits.deeplink
      : (base && base.deeplink) || ''),
    lang,
    list: (field) => {
      const e = edits && edits.whitelist;
      if (e && e[field] !== undefined) return e[field];
      return (base && base.whitelist && base.whitelist[field]) || [];
    },
    name: () => (edits && edits.name !== undefined ? edits.name : baseName()),
    vertical: () => (edits && edits.vertical) || (base && base.vertical) || fallbackVertical,
  };
};

// The full FE product (base merged with edits) that "Send for approval" submits.
export const resolveProductFE = (
  base: any,
  edits: any,
  langs: string[],
  fallbackVertical: string,
): ProductFE => {
  const values = productValues(base, edits, langs, fallbackVertical);
  const byLang = {};
  langs.forEach((lang) => {
    const name = values.lang(lang, 'name');
    const overview = values.lang(lang, 'overview');
    const entry = {
      name,
      ciEnabled: Boolean(values.lang(lang, 'ciEnabled')) && Boolean(name.trim() && overview.trim()),
      overview,
      reason: values.lang(lang, 'reason'),
      cta: values.lang(lang, 'cta'),
    };
    // Only include a language once it has a name — the backend requires a
    // name per provided language, so empty/untouched tabs are skipped.
    if (entry.name.trim()) byLang[lang] = entry;
  });
  const whitelist = {};
  PRODUCT_LIST_FIELDS.forEach((field) => { whitelist[field] = values.list(field); });
  return {
    name: values.name().trim(),
    vertical: values.vertical(),
    deeplink: values.deeplink(),
    byLang,
    whitelist,
  };
};

// Same dirty check as the detail view's hasEdits: link lists compare as sets,
// so an edit sandbox restored to the original values counts as no change.
const sameStringSet = (a: string[], b: string[]) => a.length === b.length
  && [...a].sort().join('\u0000') === [...b].sort().join('\u0000');

export const hasProductEdits = (
  base: any,
  edits: any,
  langs: string[],
  fallbackVertical: string,
): boolean => {
  if (!edits) return false;
  const edited = productValues(base, edits, langs, fallbackVertical);
  const saved = productValues(base, null, langs, fallbackVertical);
  return edited.name() !== saved.name()
    || edited.deeplink() !== saved.deeplink()
    || edited.vertical() !== saved.vertical()
    || langs.some((lang) => PRODUCT_LANG_FIELDS
      .some((field) => edited.lang(lang, field) !== saved.lang(lang, field)))
    || ['cvps', 'attrs', 'personas']
      .some((field) => !sameStringSet(edited.list(field), saved.list(field)))
    || !sameAudienceAssociations(edited.list('audiences'), saved.list('audiences'))
    || !sameFunnelObjectives(edited.list('objectives'), saved.list('objectives'));
};

export interface ProductEditChangesPlan {
  disabledReason: string;
  mode: EditChangesMode;
  seed: any;
}

// Recall and Edit Changes both drop the pending version, so while either
// version carries unsaved local edits they are disabled until the user
// discards them; otherwise those edits would be silently lost.
export const getPendingActionsDisabledReason = (
  sel: any,
  editsByVersion: Record<string, any>,
  langs: string[],
  fallbackVertical: string,
): string => {
  const hasLocalEdits = (sel.active
      && hasProductEdits(sel.active, editsByVersion.active, langs, fallbackVertical))
    || (sel.pending
      && hasProductEdits(sel.pending, editsByVersion.pending, langs, fallbackVertical));
  return hasLocalEdits ? editChangesText('disabled_local_edits') : '';
};

// Edit Changes brings the pending change back exactly as it was submitted.
// With an ACTIVE version it comes back as an edit sandbox over that version
// holding only the values that differ, so the dirty check and diff stay exact;
// without one it becomes an unkeyed in-memory draft that resubmits as a create.
export const getProductEditChangesPlan = (
  sel: any,
  editsByVersion: Record<string, any>,
  langs: string[],
  fallbackVertical: string,
): ProductEditChangesPlan => {
  const disabledReason = getPendingActionsDisabledReason(
    sel,
    editsByVersion,
    langs,
    fallbackVertical,
  );
  const pendingEdits = editsByVersion.pending || null;
  if (!sel.active) {
    return {
      disabledReason,
      mode: 'draft',
      seed: {
        ...resolveProductFE(sel.pending, pendingEdits, langs, fallbackVertical),
        country: sel.pending.country,
      },
    };
  }

  const recalled = productValues(sel.pending, pendingEdits, langs, fallbackVertical);
  const active = productValues(sel.active, null, langs, fallbackVertical);
  const seed: any = { whitelist: {} };
  if (recalled.name() !== active.name()) seed.name = recalled.name();
  if (recalled.deeplink() !== active.deeplink()) seed.deeplink = recalled.deeplink();
  if (recalled.vertical() !== active.vertical()) seed.vertical = recalled.vertical();
  langs.forEach((lang) => {
    PRODUCT_LANG_FIELDS.forEach((field) => {
      const value = recalled.lang(lang, field);
      if (value === active.lang(lang, field)) return;
      seed.byLang = seed.byLang || {};
      seed.byLang[lang] = { ...(seed.byLang[lang] || {}), [field]: value };
    });
  });
  PRODUCT_LIST_FIELDS.forEach((field) => { seed.whitelist[field] = recalled.list(field); });
  return { disabledReason, mode: 'edits', seed };
};

export interface GenerationSourceOption {
  key: GenerationConfigSource;
  // Label and tone mirror the status chip on the detail header, so the picker
  // shows the same tag the user is already looking at.
  label: string;
  tone: 'active' | 'pending' | 'draft';
  description: string;
}

// The versions a run may be generated from. Only the published version can be
// resolved from Mystique's cache; the rest are sent inline.
// A version is only offered once it carries everything generation needs, so an
// incomplete one is never selectable.
export const buildGenerationSourceOptions = ({
  activeGeneratable,
  editingPending,
  hasActive,
  hasEdits,
  hasPending,
  isDraft,
  pendingGeneratable,
  sandboxGeneratable,
}: {
  activeGeneratable: boolean;
  editingPending: boolean;
  hasActive: boolean;
  hasEdits: boolean;
  hasPending: boolean;
  isDraft: boolean;
  pendingGeneratable: boolean;
  sandboxGeneratable: boolean;
}): GenerationSourceOption[] => {
  if (isDraft) {
    if (!sandboxGeneratable) return [];
    return [{
      key: 'draft',
      description: 'The unsaved draft on screen. Nothing is saved by generating.',
      label: 'DRAFT',
      tone: 'draft',
    }];
  }

  const options: GenerationSourceOption[] = [];
  if (hasActive && activeGeneratable) {
    options.push({
      key: 'active',
      description: 'The approved configuration currently live.',
      label: 'ACTIVE',
      tone: 'active',
    });
  }
  if (hasPending && pendingGeneratable) {
    options.push({
      key: 'pending',
      description: 'The configuration awaiting approval, as submitted.',
      label: 'PENDING APPROVAL',
      tone: 'pending',
    });
  }
  if (hasEdits && sandboxGeneratable) {
    options.push({
      key: 'local-edits',
      description: editingPending
        ? 'Your unsaved edits on top of the pending configuration.'
        : 'Your unsaved edits on top of the published configuration.',
      label: 'LOCAL EDITS',
      tone: 'draft',
    });
  }
  return options;
};

export const getContentGenerationState = ({
  anySourceGeneratable,
  generationInProgressElsewhere,
  hasActive,
  hasEdits,
  hasGeneratableLanguage,
  hasPending,
  isDraft,
  langCount,
  showPending,
}: {
  // Whether at least one version offered by the picker can generate. The
  // on-screen version must not block a run from a different, valid one.
  anySourceGeneratable: boolean;
  generationInProgressElsewhere: boolean;
  hasActive: boolean;
  hasEdits: boolean;
  hasGeneratableLanguage: boolean;
  hasPending: boolean;
  isDraft: boolean;
  langCount: number;
  showPending: boolean;
}): ContentGenerationState => {
  let configSource: GenerationConfigSource = 'active';
  if (isDraft) configSource = 'draft';
  else if (hasEdits) configSource = 'local-edits';
  else if (hasPending && (showPending || !hasActive)) configSource = 'pending';

  let disabledReason = '';
  if (langCount === 0) {
    disabledReason = 'No languages are configured for this country.';
  } else if (!anySourceGeneratable) {
    // Nothing is generatable, so describe the version on screen.
    disabledReason = !hasGeneratableLanguage
      ? 'Add a product name and overview before generating content.'
      : 'Select at least one CVP before generating content.';
  } else if (generationInProgressElsewhere) {
    disabledReason = 'Content generation is already in progress for another product offering.';
  }

  return { configSource, disabledReason };
};

const ProductOfferingPage = ({
  contentConfigPermissions = EMPTY_CONTENT_CONFIG_PERMISSIONS,
  navTick,
}) => {
  const { canCreate, canEditLocally, canUpdate, canReview } = contentConfigPermissions;
  const dispatch = useDispatch();
  const useUcdContentGeneration = useContentGenerationEndpointMigrationEnabled();
  const userEmail = useSelector((s) => _.get(s, 'app.user.email', ''));
  const contentGenerationConfigLatest = useSelector((s) =>
    _.get(s, 'ucd.contentGenerationConfigLatest', EMPTY_OBJECT));
  const contentGenerationConfigLatestRequest = useSelector((s) =>
    _.get(s, `requests.${getRequestStateKey(CONTENT_GENERATION_CONFIG_GET_LATEST)}`, EMPTY_OBJECT));
  const baseConfigVersion = Number(_.get(contentGenerationConfigLatest, 'version', 0)) || 0;
  const contentConfigAudiencesEnabled = useHolosPortalFeatureFlag(
    'enabledContentConfigAudiences',
  );
  const productOfferingAudiencesEnabled = useHolosPortalFeatureFlag(
    'enabledProductOfferingAudiences',
  );
  const productOfferingObjectivesEnabled = useHolosPortalFeatureFlag(
    'enabledProductOfferingObjectives',
  );

  const [country, setCountry] = useState('');
  // Deep-link params (?country=SG&key=...) captured once on mount so a shared
  // URL opens the config directly. Consumed once the data loads; cleared on
  // any manual navigation so a stale link can't hijack the view later.
  const [deepLink, setDeepLink] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return { country: params.get('country') || '', key: params.get('key') || '' };
  });
  const clearDeepLink = useCallback(() => setDeepLink({ country: '', key: '' }), []);

  // ---- reference + product data from the store (memoised so the adapter maps
  // only re-run when the selected slice actually changes, not every render) ----
  const cpVerticals = useSelector((s) => _.get(s, 'ucd.cpVerticals', EMPTY_ARRAY));
  const cpCvps = useSelector((s) => _.get(s, 'ucd.cpCvps', EMPTY_ARRAY));
  const cpAttributes = useSelector((s) => _.get(s, 'ucd.cpAttributes', EMPTY_ARRAY));
  const countryActiveAudiencesRaw = useSelector((s) =>
    _.get(s, ['ucd', 'cpAudiencesByScope', country, 'active'], EMPTY_ARRAY));
  const cpPersonas = useSelector((s) => _.get(s, 'ucd.cpPersonas', EMPTY_ARRAY));
  const cpCountries = useSelector((s) => _.get(s, 'ucd.cpCountries', EMPTY_ARRAY));
  const allActiveAudiencesRaw = useSelector((s) =>
    _.get(s, ['ucd', 'cpAudiencesByScope', '__all__', 'active'], EMPTY_ARRAY));
  const activeCvpsRaw = useSelector((s) => _.get(s, 'ucd.cpCvpsByCountry.__all__.active', EMPTY_ARRAY));
  const verticals = useMemo(() => cpVerticals.map(verticalFromApi), [cpVerticals]);
  const cvpOptions = useMemo(
    () => (activeCvpsRaw.length ? activeCvpsRaw : cpCvps).map(cvpFromApi).filter((c) => isActive(c.status)),
    [activeCvpsRaw, cpCvps],
  );
  const attributeAudiencesRaw = useMemo(
    () => (productOfferingAudiencesEnabled
      ? allActiveAudiencesRaw.filter((config) => {
        const audienceCountry = _.get(config, 'meta.country', '');
        return !audienceCountry || audienceCountry === country;
      })
      : countryActiveAudiencesRaw),
    [
      allActiveAudiencesRaw,
      country,
      countryActiveAudiencesRaw,
      productOfferingAudiencesEnabled,
    ],
  );
  const attrOptions = useMemo(() => productAttributeOptionsFromApi({
    attributes: cpAttributes,
    audiences: attributeAudiencesRaw,
    audiencesEnabled: contentConfigAudiencesEnabled,
  }), [attributeAudiencesRaw, contentConfigAudiencesEnabled, cpAttributes]);
  const personaOptions = useMemo(() => cpPersonas.map(personaFromApi), [cpPersonas]);
  const countries = useMemo(
    () => cpCountries.map(countryFromApi).sort(localeSorter('name')),
    [cpCountries],
  );
  const audienceReferences = useMemo(
    () => allActiveAudiencesRaw.map(audienceReferenceFromApi),
    [allActiveAudiencesRaw],
  );
  const activeRaw = useSelector((s) => _.get(s, `ucd.cpProductsByCountry.${country}.active`, EMPTY_ARRAY));
  const pendingRaw = useSelector((s) => _.get(s, `ucd.cpProductsByCountry.${country}.pending`, EMPTY_ARRAY));
  const activeListReq = useSelector((s) => _.get(s, 'requests.cpListProductsRequest', EMPTY_OBJECT));
  const pendingListReq = useSelector((s) => _.get(s, 'requests.cpListProductsPendingRequest', EMPTY_OBJECT));
  const audienceListReq = useSelector((s) =>
    _.get(s, 'requests.cpListAudiencesRequest', EMPTY_OBJECT));
  const generationRequest = useSelector((s) =>
    _.get(s, 'requests.contentConfigGenerateMystiqueV2Request', EMPTY_OBJECT));
  const generationResponsesByAudienceKey = useSelector((s) =>
    _.get(s, 'ucd.contentConfigGenerationByAttribute', EMPTY_OBJECT));
  const generationReadyAudienceKeys = useSelector((s) =>
    _.get(s, 'ucd.contentConfigGenerationReadyAttributes', EMPTY_ARRAY));
  const generationFailedAudienceKeys = useSelector((s) =>
    _.get(s, 'ucd.contentConfigGenerationFailedAttributes', EMPTY_ARRAY));
  const generationErrorsByAudienceKey = useSelector((s) =>
    _.get(s, 'ucd.contentConfigGenerationErrorsByAttribute', EMPTY_OBJECT));
  // Active and pending lists are fetched separately; the list is loading until
  // both have settled.
  const listPending = Boolean(activeListReq.isPending) || Boolean(pendingListReq.isPending);

  // ---- fetch on mount + when country changes ----
  useEffect(() => {
    dispatch(cpGetVerticals());
    dispatch(cpGetPersonas());
    if (!contentConfigAudiencesEnabled) dispatch(cpListAttributes());
    dispatch(cpGetCountries()); // needed for per-country language tabs + country selector
    dispatch(cpListCvps('')); // CVPs are global — fetch the full list, not per-country
  }, [contentConfigAudiencesEnabled, dispatch]);
  useEffect(() => {
    if (
      contentConfigAudiencesEnabled
      && !productOfferingAudiencesEnabled
      && country
    ) {
      dispatch(cpListAudiences(country));
    }
  }, [
    contentConfigAudiencesEnabled,
    country,
    dispatch,
    productOfferingAudiencesEnabled,
  ]);
  const refetchAudiences = useCallback(() => {
    dispatch(cpListAudiences());
  }, [dispatch]);
  useEffect(() => {
    if (!country) return;
    refetchAudiences();
  }, [country, refetchAudiences]);
  const refetchProducts = useCallback(() => {
    if (!country) return;
    dispatch(cpListProducts(country));
    dispatch(cpListProducts(country, STATUS_PENDING));
  }, [country, dispatch]);
  // Default the selected country to the deep-linked one when valid, else the
  // first one (countries are sorted by name).
  useEffect(() => {
    if (!country && countries.length) {
      const linked = deepLink.country && countries.some((c) => c.id === deepLink.country)
        ? deepLink.country
        : '';
      setCountry(linked || countries[0].id);
    }
  }, [countries, country, deepLink.country]);
  useEffect(() => {
    refetchProducts();
  }, [refetchProducts]);

  // ---- merge active + pending rows by logical key ----
  // In-memory draft for a brand-new offering (country + vertical + name from the
  // modal). It is authored in the detail view and only persisted (via
  // CreateProduct) on "Send for approval"; not stored on the backend until then.
  const [draft, setDraft] = useState(null);

  const merged = useMemo(() => {
    const m = {};
    activeRaw.map(productFromApi).forEach((p) => {
      m[p.key] = { ...(m[p.key] || {}), key: p.key, vertical: p.vertical, active: p };
    });
    pendingRaw.map(productFromApi).forEach((p) => {
      m[p.key] = { ...(m[p.key] || {}), key: p.key, vertical: p.vertical, pending: p };
    });
    if (draft) m[DRAFT_KEY] = { key: DRAFT_KEY, vertical: draft.vertical, draft };
    return m;
  }, [activeRaw, pendingRaw, draft]);

  // Languages come straight from the selected country's API config — no fallback.
  const langs = useMemo(() => {
    const c = countries.find((x) => x.id === country);
    return (c && c.langs) ? c.langs : [];
  }, [countries, country]);
  // The same country's UCM whitelist verbatim. `langs` above is synthesised
  // (en-first) for the config_by_lang tabs; an association `lang` is matched
  // byte-for-byte by UCD, so it must come from here instead.
  const countryLangs = useMemo(() => {
    const c = countries.find((x) => x.id === country);
    return (c && c.apiLangs) ? c.apiLangs : [];
  }, [countries, country]);

  // selectedKey === '' → list (table) view; otherwise the detail view for that key.
  const [selectedKey, setSelectedKey] = useState('');
  const [activeLang, setActiveLang] = useState('');
  const [editsByVersion, setEditsByVersion] = useState({}); // local sandboxes, one per version
  const [showNew, setShowNew] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [showGenerationSetup, setShowGenerationSetup] = useState(false);
  const [showGeneration, setShowGeneration] = useState(false);
  const [generationRun, setGenerationRun] = useState(EMPTY_GENERATION_RUN);
  const [generationSetupInitialAudienceKeys, setGenerationSetupInitialAudienceKeys] = useState(EMPTY_STRING_ARRAY);
  const [isRegeneration, setIsRegeneration] = useState(false);
  // Version chosen in the setup modal; '' falls back to the on-screen default.
  const [generationSetupSource, setGenerationSetupSource] = useState('');
  const [listTab, setListTab] = useState('all'); // 'all' | 'pending' | 'ci-enabled'
  const [verticalFilter, setVerticalFilter] = useState(''); // '' = all verticals
  // When a pending change exists, the editor shows the active (published)
  // version by default; "See changes" flips it to the pending version.
  const [showPending, setShowPending] = useState(false);

  // Clicking the "Product Offerings" nav item (even while already on it) returns
  // to the list — there's no in-page back button.
  useEffect(() => { setSelectedKey(''); setEditsByVersion({}); }, [navTick]);
  // If the open offering no longer exists for this country (recall/reject, or a
  // stale selection after a refetch), drop back to the list.
  useEffect(() => {
    if (selectedKey && selectedKey !== DRAFT_KEY && !merged[selectedKey] && !listPending) {
      setSelectedKey('');
    }
  }, [listPending, merged, selectedKey]);
  // Open the deep-linked config once it shows up in the merged rows. Waiting
  // for the key (rather than gating on listPending) avoids racing the
  // stale-selection effect above before the fetch has started. Once a full
  // load cycle settles without the key, the link is invalid — drop it.
  const sawListLoad = useRef(false);
  useEffect(() => {
    if (!deepLink.key) return;
    if (merged[deepLink.key]) {
      setSelectedKey(deepLink.key);
      clearDeepLink();
      return;
    }
    if (listPending) sawListLoad.current = true;
    else if (sawListLoad.current) clearDeepLink();
  }, [clearDeepLink, deepLink.key, listPending, merged]);
  // Bumped on every history change so the share-param sync below re-evaluates
  // after external navigation: the ucd top tabs keep their panes mounted, so a
  // tab switch rewrites the URL (stripping our params) without changing any of
  // this page's state — returning to the tab must restore them.
  const [locTick, setLocTick] = useState(0);
  useEffect(() => {
    // listen() returns an unlisten function; returning it unsubscribes on unmount.
    const unlisten = browserHistory.listen(() => setLocTick((t) => t + 1));
    return unlisten;
  }, []);
  // Keep the address bar shareable: an open (non-draft) config is reflected as
  // ?country=..&key=.. so the URL can be copied directly from the browser. Both
  // params are stripped on the list view so they don't follow the user to other
  // sections (deep links survive — they're captured into state on mount above).
  // replace (not push) — detail open/close is component state, not routing.
  useEffect(() => {
    if (deepLink.key) return; // don't strip params before the deep link is consumed
    const params = new URLSearchParams(window.location.search);
    // Only own the URL while content-config is the active ucd tab — this page
    // stays mounted (hidden) under other tabs, whose URLs must stay clean.
    if (params.get('tab') !== 'content-config') return;
    const before = params.toString();
    if (country && selectedKey && selectedKey !== DRAFT_KEY) {
      params.set('country', country);
      params.set('key', selectedKey);
    } else {
      params.delete('key');
      params.delete('country');
    }
    const after = params.toString();
    if (after !== before) browserHistory.replace(`${window.location.pathname}?${after}`);
  }, [country, deepLink.key, locTick, selectedKey]);
  // Strip the share params when this page unmounts (e.g. switching to another
  // content-config section) — they describe a view that is no longer on screen
  // and would otherwise linger. Scoped to the content-config tab URL: on route
  // transitions window.location is already the destination, whose country/key
  // params (if any) belong to that route, not to this page.
  useEffect(() => () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') !== 'content-config') return;
    if (!params.has('key') && !params.has('country')) return;
    params.delete('key');
    params.delete('country');
    const qs = params.toString();
    browserHistory.replace(`${window.location.pathname}${qs ? `?${qs}` : ''}`);
  }, []);
  useEffect(() => {
    if (!langs.length) {
      if (activeLang) setActiveLang('');
      return;
    }
    if (!langs.includes(activeLang)) setActiveLang(langs[0]);
  }, [activeLang, langs]);
  useEffect(() => {
    setEditsByVersion({});
    setShowDiff(false);
    setShowPending(false);
    // A run belongs to one offering: dropping it here covers recall/reject
    // refetches, a submitted draft, and any stale selection.
    setShowGeneration(false);
    setGenerationRun(EMPTY_GENERATION_RUN);
  }, [selectedKey, country]);

  const sel = merged[selectedKey];
  const isDraft = Boolean(sel && sel.draft && !sel.active && !sel.pending);
  // Displayed/edited record — active (published) by default; the pending version
  // when "See changes" is toggled on.
  const base = sel && (showPending && sel.pending ? sel.pending : (sel.active || sel.pending || sel.draft));
  const defaultLang = langs[0];
  const hasPending = Boolean(sel && sel.pending);
  const pendingAuthor = hasPending ? (sel.pending.updatedBy || sel.pending.createdBy) : null;
  const pendingExpiry = hasPending ? formatExpiry(sel.pending.expiresAt) : '';
  const isMine = pendingAuthor === userEmail;
  // Edits are sandboxed per version so a pending offering can be edited for a
  // test generation without those edits leaking onto the published version when
  // the user toggles back with "See active".
  const editVersionKey = getEditVersionKey(isDraft, base, sel);
  const edits = editsByVersion[editVersionKey] || null;
  const setEdits = (value) => setEditsByVersion((prev) => ({
    ...prev,
    [editVersionKey]: typeof value === 'function' ? value(prev[editVersionKey] || null) : value,
  }));

  const onWriteSuccess = useCallback(({ editChanges, toList }: {
    editChanges?: EditChangesSeed<any>;
    toList?: boolean;
  } = {}) => {
    // Skip seeding if the user moved to another offering while the recall was
    // in flight; the refetch below still drops the recalled change.
    if (editChanges && editChanges.rowKey === selectedKey) {
      setShowPending(false);
      setShowDiff(false);
      // The run may have been generated from the pending version that is gone.
      setShowGeneration(false);
      setGenerationRun(EMPTY_GENERATION_RUN);
      if (editChanges.mode === 'draft') {
        // Unkeyed in-memory draft: "Send for approval" creates it again under
        // a new key. Changing the selection clears the edit sandboxes.
        setDraft(editChanges.value);
        setSelectedKey(DRAFT_KEY);
      } else {
        setEditsByVersion({ active: editChanges.value });
      }
      message.success(editChangesText('success'));
    } else if (!editChanges) {
      setEditsByVersion({});
      setDraft(null);
      if (toList) setSelectedKey('');
    }
    refetchProducts();
  }, [refetchProducts, selectedKey]);
  const onWriteFailure = useCallback(({ editChanges }: {
    editChanges?: EditChangesSeed<any>;
  } = {}) => {
    if (!editChanges) return;
    message.error(editChangesText('error'));
    refetchProducts();
  }, [refetchProducts]);

  const { busy, runWrite } = useContentConfigWrite({
    onFailure: onWriteFailure,
    onSuccess: onWriteSuccess,
  });
  const notifyForbidden = () => message.error(FORBIDDEN_MESSAGE);

  // ---- value resolution (sandbox over base) ----
  const langVal = (lang, field) => {
    const e = edits && edits.byLang && edits.byLang[lang];
    if (e && e[field] !== undefined) return e[field];
    const b = base && base.byLang && base.byLang[lang];
    return b && b[field] !== undefined ? b[field] : '';
  };
  const baseLangVal = (lang, field) => {
    const b = base && base.byLang && base.byLang[lang];
    return b && b[field] !== undefined ? b[field] : '';
  };
  const baseNameVal = () => (base && base.name) || '';
  const baseResolvedNameVal = () => baseNameVal() || baseLangVal(defaultLang || 'en', 'name');
  const nameVal = () => {
    if (edits && edits.name !== undefined) return edits.name;
    return baseResolvedNameVal();
  };
  const baseDeeplinkVal = () => (base && base.deeplink) || '';
  const deeplinkVal = () => {
    if (edits && edits.deeplink !== undefined) return edits.deeplink;
    return baseDeeplinkVal();
  };
  const listVal = (field) => {
    const e = edits && edits.whitelist;
    if (e && e[field] !== undefined) return e[field];
    return (base && base.whitelist && base.whitelist[field]) || [];
  };
  const setNameEdit = (value) => setEdits((prev) => ({ ...(prev || {}), name: value }));
  const setDeeplinkEdit = (value) => setEdits((prev) => ({
    ...(prev || {}), deeplink: value,
  }));
  const setLang = (lang, field, value) => setEdits((prev) => ({
    ...(prev || {}),
    byLang: { ...((prev || {}).byLang || {}), [lang]: { ...(((prev || {}).byLang || {})[lang] || {}), [field]: value } },
  }));
  const setList = (field, value) => setEdits((prev) => ({
    ...(prev || {}), whitelist: { ...((prev || {}).whitelist || {}), [field]: value },
  }));
  // Vertical lives in the same edit sandbox — changing it via the dropdown
  // stages a change (a pending update for an active product, or just edits the
  // draft) that "Send for approval" submits.
  const verticalVal = () => (edits && edits.vertical) || (base && base.vertical) || (verticals[0] && verticals[0].id);
  const setVerticalEdit = (v) => setEdits((prev) => ({ ...(prev || {}), vertical: v }));

  // ---- dirty check ----
  // Compare resolved (sandbox-over-base) values against the base record rather
  // than just "has the sandbox been touched". Restoring a field to its original
  // value clears the dirty state, so an unchanged update can't be submitted.
  const baseListVal = (field) => (base && base.whitelist && base.whitelist[field]) || [];
  const baseVerticalVal = () => (base && base.vertical) || (verticals[0] && verticals[0].id);
  // Link lists are compared as sets — reordering selections is not a change.
  const sameSet = (a, b) => a.length === b.length
    && [...a].sort().join('\u0000') === [...b].sort().join('\u0000');
  const countryName = useCallback(
    (cid) => (countries.find((c) => c.id === cid) || {}).name || cid,
    [countries],
  );
  const audienceLabelCopy = useMemo(() => ({
    regional: i18n.t('ucd_content_config.product_offerings.audiences.regional'),
    segment: i18n.t('ucd_content_config.product_offerings.audiences.segment'),
    table: i18n.t('ucd_content_config.product_offerings.audiences.table'),
    unavailablePrefix: i18n.t(
      'ucd_content_config.product_offerings.audiences.unavailable_prefix',
    ),
    unknownSource: i18n.t('ucd_content_config.product_offerings.audiences.unknown_source'),
  }), []);
  const selectedAudienceAssociations = listVal('audiences');
  const audienceOptions = useMemo(
    () => buildProductAudienceOptions(
      audienceReferences,
      selectedAudienceAssociations,
      country,
      countryName,
      audienceLabelCopy,
    ),
    [
      audienceLabelCopy,
      audienceReferences,
      country,
      countryName,
      selectedAudienceAssociations,
    ],
  );
  const audienceOptionByKey = useMemo(
    () => new Map(audienceOptions.map((option) => [option.key, option])),
    [audienceOptions],
  );
  const audienceLabelForKey = useCallback((key) => {
    const option = audienceOptionByKey.get(key);
    if (option) return option.label;
    const reference = audienceReferences.find((audience) => audience.key === key);
    return reference
      ? audienceDisplayLabel(reference, countryName, audienceLabelCopy)
      : `${audienceLabelCopy.unavailablePrefix} · ${key}`;
  }, [audienceLabelCopy, audienceOptionByKey, audienceReferences, countryName]);
  const {
    audienceValidationUnavailable,
    hasInvalidAudiences,
  } = getProductAudienceValidationState(
    productOfferingAudiencesEnabled,
    Boolean(audienceListReq.isSuccessful),
    selectedAudienceAssociations,
    audienceOptionByKey,
  );
  const hasInvalidAudienceReasons = productOfferingAudiencesEnabled
    && selectedAudienceAssociations.some(
      ({ reasonToUse }) => !isAudienceReasonValid(reasonToUse),
    );
  // Mirrors exactly what buildFE persists as `ci_enabled`: the toggle alone is
  // not enough, the language also needs a name and an overview. Derived from
  // the live editor state so the warning tracks unsaved CI toggles.
  const editorCiEnabledLangs = useMemo(
    () => langs.filter((lang) => Boolean(langVal(lang, 'ciEnabled'))
      && Boolean(String(langVal(lang, 'name')).trim())
      && Boolean(String(langVal(lang, 'overview')).trim())),
    [base, edits, langs], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const hasInvalidAudienceLangs = hasInvalidProductAudienceLangs(
    productOfferingAudiencesEnabled,
    countryLangs,
    selectedAudienceAssociations,
  );
  const productNameReady = Boolean(nameVal().trim());
  const hasInvalidDeeplink = !isProductDeeplinkValid(deeplinkVal());
  const hasEdits = Boolean(edits) && (
    nameVal() !== baseResolvedNameVal()
    || deeplinkVal() !== baseDeeplinkVal()
    || verticalVal() !== baseVerticalVal()
    || langs.some((lang) => ['name', 'ciEnabled', 'overview', 'reason', 'cta']
      .some((field) => langVal(lang, field) !== baseLangVal(lang, field)))
    || ['cvps', 'attrs', 'personas'].some((field) => !sameSet(listVal(field), baseListVal(field)))
    || !sameAudienceAssociations(listVal('audiences'), baseListVal('audiences'))
    || !sameFunnelObjectives(listVal('objectives'), baseListVal('objectives'))
  );

  const fallbackVertical = verticals[0] && verticals[0].id;
  // Build a full FE product (base merged with edits) for submission.
  const buildFE = () => resolveProductFE(base, edits, langs, fallbackVertical);

  // Each source maps to one FE product record. Only 'active' is resolvable from
  // Mystique's cache, so every other source travels inline.
  const generationRecordForSource = (source) => {
    if (source === 'pending') return sel && sel.pending;
    if (source === 'active') return sel && sel.active;
    if (source === 'draft') return buildFE();
    if (source === 'local-edits') {
      // Keep the identity of the version the edits sit on, so a generation run
      // from unsaved edits is still attributable to its product offering.
      const edited = editVersionKey === 'pending' ? (sel && sel.pending) : (sel && sel.active);
      return { ...buildFE(), key: (edited && edited.key) || '', status: edited && edited.status };
    }
    return base;
  };

  // Draft is ready to submit once the default language has a name + overview.
  const draftReady = isDraft
    && productNameReady
    && Boolean(langVal(defaultLang, 'name').trim() && langVal(defaultLang, 'overview').trim());
  const productSubmitState = {
    isDraft,
    draftReady,
    hasActive: Boolean(sel && sel.active),
    hasEdits,
    hasPending,
    hasInvalidDeeplink,
    hasInvalidAudiences,
    hasInvalidAudienceReasons,
    hasInvalidAudienceLangs,
  };

  const openDetail = (key) => { setSelectedKey(key); setEditsByVersion({}); clearDeepLink(); };
  const onCountryChange = (c) => {
    setCountry(c); setSelectedKey(''); setEditsByVersion({}); setDraft(null); clearDeepLink();
  };
  const discardDraft = () => { setDraft(null); setEditsByVersion({}); setSelectedKey(''); };

  const onSendForApproval = () => {
    if (isDraft) {
      if (!canCreate) {
        notifyForbidden();
        return;
      }
      if (!productNameReady) {
        message.error('Smart Comms tagged name is required.');
        return;
      }
      if (!canSubmitProductForApproval(contentConfigPermissions, productSubmitState)) return;
      runWrite(cpCreateProduct(country, productToApiBody(buildFE()), userEmail), 'createProduct', { toList: true });
      return;
    }
    if (!sel || !sel.active) return;
    if (!canUpdate) {
      notifyForbidden();
      return;
    }
    if (!productNameReady) {
      message.error('Smart Comms tagged name is required.');
      return;
    }
    if (!canSubmitProductForApproval(contentConfigPermissions, productSubmitState)) return;
    runWrite(cpUpdateProduct(sel.active.id, productToApiBody(buildFE()), userEmail), 'updateProduct');
  };
  const onApprove = () => {
    if (!sel || !sel.pending) return;
    if (!canReviewPendingChange(contentConfigPermissions, isMine)) {
      notifyForbidden();
      return;
    }
    runWrite(cpApprove(sel.pending.id, userEmail), 'approve');
  };
  const pendingActionsDisabledReason = sel && sel.pending
    ? getPendingActionsDisabledReason(sel, editsByVersion, langs, fallbackVertical)
    : '';
  const onRecall = () => {
    if (!sel || !sel.pending) return;
    if (!canRecallPendingChange(contentConfigPermissions, isMine)) {
      notifyForbidden();
      return;
    }
    if (pendingActionsDisabledReason) {
      message.error(pendingActionsDisabledReason);
      return;
    }
    runWrite(cpRecall(sel.pending.id, userEmail), 'recall');
  };
  // Edit Changes = Recall + reopen the recalled change as a DRAFT.
  const onEditChanges = () => {
    if (!sel || !sel.pending) return;
    if (!canRecallPendingChange(contentConfigPermissions, isMine)) {
      notifyForbidden();
      return;
    }
    // Capture before recalling: UCD soft-deletes the pending row on success.
    const plan = getProductEditChangesPlan(sel, editsByVersion, langs, fallbackVertical);
    if (plan.disabledReason) {
      message.error(plan.disabledReason);
      return;
    }
    const editChanges: EditChangesSeed<any> = {
      mode: plan.mode,
      rowKey: selectedKey,
      value: plan.seed,
    };
    runWrite(cpRecall(sel.pending.id, userEmail), 'recall', { editChanges });
  };
  const onReject = () => {
    if (!sel || !sel.pending) return;
    if (!canReviewPendingChange(contentConfigPermissions, isMine)) {
      notifyForbidden();
      return;
    }
    const pendingId = sel.pending.id;
    let comment = '';
    Modal.confirm({
      title: 'Reject pending change?',
      content: (<Input.TextArea placeholder="Reason (required)" onChange={(e) => { comment = e.target.value; }} />),
      okText: 'Reject',
      okType: 'danger',
      onOk: () => {
        if (!canReviewPendingChange(contentConfigPermissions, isMine)) { notifyForbidden(); return Promise.reject(); }
        if (!comment.trim()) { message.error('A rejection comment is required.'); return Promise.reject(); }
        runWrite(cpReject(pendingId, userEmail, comment), 'reject');
        return Promise.resolve();
      },
    });
  };
  const verticalName = (vid) => (verticals.find((v) => v.id === vid) || {}).name || vid;
  // The deep link Content Injection falls back to when this offering defines
  // none of its own (campaignGenerator.getDeeplink -> GetContentInjectionDeeplink
  // -> ContentPortal.Verticals[vertical].Deeplink). '' when the vertical cannot
  // be resolved or UCM has no value for it. Display-only: it is never written
  // into editor state, so it cannot be persisted onto the offering.
  const verticalDeeplink = (vid) => (verticals.find((v) => v.id === vid) || {}).deeplink || '';
  // The language Content Injection resolves an untargeted Audience association
  // to (effectiveAudienceLanguage: association lang -> country default). ''
  // when the country cannot be resolved or UCM has no default for it. Not the
  // editor's `defaultLang` above, which is the en-first tab. Display-only, like
  // `verticalDeeplink`: never written into editor state or the product body.
  const countryDefaultLang = (cid) => (countries.find((c) => c.id === cid) || {}).defaultLang || '';

  // Content generation defaults to what the editor is showing: the published
  // version, the pending version while reviewing changes, an unsaved draft, or
  // unsaved local edits. Only the published version can be resolved from
  // Mystique's cache; the rest travel inline with the request.
  const whitelistOf = (record, field) => (
    (record && record.whitelist && record.whitelist[field]) || EMPTY_ARRAY
  );
  // Drives the setup modal's option lists, so it follows the chosen version.
  const setupWhitelistVal = (field) => {
    if (generationSetupSource === 'active') return whitelistOf(sel && sel.active, field);
    if (generationSetupSource === 'pending') return whitelistOf(sel && sel.pending, field);
    // draft, local-edits, or nothing chosen yet: the live sandbox, whose
    // arrays are stable references from the store or the edit state.
    return listVal(field);
  };
  // resolveGenerationObjectives allocates, so this must be memoised on the
  // (stable) source array: an unmemoised call would hand the setup modal a new
  // objectives identity every render and re-run its seeding effect each time.
  const setupObjectives = setupWhitelistVal('objectives');
  const generationObjectives = useMemo(
    () => getGenerationObjectives(productOfferingObjectivesEnabled, setupObjectives),
    [productOfferingObjectivesEnabled, setupObjectives],
  );
  const generationObjectiveOptions = useMemo(
    () => buildGenerationObjectiveOptions(generationObjectives),
    [generationObjectives],
  );
  const generationDefaultObjectiveID = useMemo(() => {
    if (generationObjectiveOptions.length === 1) {
      return generationObjectiveOptions[0].id;
    }
    return '';
  }, [generationObjectiveOptions]);
  const setupAudienceAssociations = setupWhitelistVal('audiences');
  const whitelistedGenerationAudienceKeys = useMemo(
    () => setupAudienceAssociations
      .map(({ audienceKey }) => audienceKey)
      .filter(Boolean),
    [setupAudienceAssociations],
  );
  const generationAudienceOptions = useMemo<ContentGenerationSetupAudienceOption[]>(() => {
    return buildGenerationAudienceOptions(
      audienceReferences,
      country,
      countryName,
      whitelistedGenerationAudienceKeys,
    );
  }, [audienceReferences, country, countryName, whitelistedGenerationAudienceKeys]);
  const generationAudienceLabelsByKey = useMemo(() => {
    const result: Record<string, string> = {};
    generationAudienceOptions.forEach((audience) => {
      result[audience.key] = audience.label;
    });
    return result;
  }, [generationAudienceOptions]);
  const resolvedGenerationDefaultAudienceKeys = useMemo(() => {
    const availableAudienceKeySet = new Set(generationAudienceOptions.map((audience) => audience.key));
    const availableWhitelistedAudienceKeys = whitelistedGenerationAudienceKeys
      .filter((audienceKey) => availableAudienceKeySet.has(audienceKey));
    return availableWhitelistedAudienceKeys;
  }, [generationAudienceOptions, whitelistedGenerationAudienceKeys]);
  // The setup modal re-seeds its checkbox selection whenever this array's
  // identity changes. Switching the configuration always produces a new source
  // array, so identity is pinned to the resolved key *values*: otherwise
  // switching between two versions whose whitelists match would silently
  // discard the audiences the user had just unchecked.
  const generationDefaultAudienceKeySignature = resolvedGenerationDefaultAudienceKeys.join('\u0000');
  const generationDefaultAudienceKeys = useMemo(
    () => resolvedGenerationDefaultAudienceKeys,
    [generationDefaultAudienceKeySignature], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (!useUcdContentGeneration) return;
    dispatch(contentGenerationConfigGetLatest());
  }, [dispatch, useUcdContentGeneration]);

  // Keyed on the selection rather than the product key: an unsaved draft has no
  // key of its own.
  const generationMatchesCurrentContext = Boolean(
    generationRun.requests.length > 0
    && generationRun.selectionKey === selectedKey
    && generationRun.context.country === country,
  );
  const generationHasFailures = generationRun.audienceKeys.some((audienceKey) =>
    generationFailedAudienceKeys.includes(audienceKey));

  useEffect(() => {
    if (!country || !selectedKey) return;
    refetchAudiences();
  }, [
    country,
    base && base.key,
    base && base.vertical,
    refetchAudiences,
    selectedKey,
  ]);

  // A language can be generated once it carries both a name and an overview.
  // Memoised per version: these feed the setup modal, whose seeding effects
  // re-run on array identity, and an unstable list would reset the user's pick.
  const activeRecord = sel && sel.active;
  const pendingRecord = sel && sel.pending;
  const hasActiveVersion = Boolean(activeRecord);
  const recordGeneratableLangs = (record) => langs.filter((lang) => {
    const byLang = (record && record.byLang && record.byLang[lang]) || {};
    return Boolean((byLang.name || '').trim() && (byLang.overview || '').trim());
  });
  const activeGeneratableLangs = useMemo(
    () => recordGeneratableLangs(activeRecord),
    [activeRecord, langs], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const pendingGeneratableLangs = useMemo(
    () => recordGeneratableLangs(pendingRecord),
    [langs, pendingRecord], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const sandboxGeneratableLangs = useMemo(
    () => langs.filter((lang) => Boolean(
      langVal(lang, 'name').trim() && langVal(lang, 'overview').trim(),
    )),
    [base, edits, langs], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const hasGeneratableLanguage = sandboxGeneratableLangs.length > 0;
  const activeGeneratable = whitelistOf(activeRecord, 'cvps').length > 0
    && activeGeneratableLangs.length > 0;
  const pendingGeneratable = whitelistOf(pendingRecord, 'cvps').length > 0
    && pendingGeneratableLangs.length > 0;
  const sandboxGeneratable = listVal('cvps').length > 0 && hasGeneratableLanguage;
  // Offer only the languages the chosen version can actually generate, so the
  // picker cannot dead-end on a language with no content.
  const generationSetupLanguages = useMemo(() => {
    if (generationSetupSource === 'active') return activeGeneratableLangs;
    if (generationSetupSource === 'pending') return pendingGeneratableLangs;
    return sandboxGeneratableLangs;
  }, [activeGeneratableLangs, generationSetupSource, pendingGeneratableLangs, sandboxGeneratableLangs]);
  const generationSourceOptions = useMemo(
    () => buildGenerationSourceOptions({
      activeGeneratable,
      editingPending: editVersionKey === 'pending',
      hasActive: hasActiveVersion,
      hasEdits,
      hasPending,
      isDraft,
      pendingGeneratable,
      sandboxGeneratable,
    }),
    [
      activeGeneratable,
      editVersionKey,
      hasActiveVersion,
      hasEdits,
      hasPending,
      isDraft,
      pendingGeneratable,
      sandboxGeneratable,
    ],
  );
  const {
    configSource: generationDefaultSource,
    disabledReason: generationDisabledReason,
  } = getContentGenerationState({
    anySourceGeneratable: generationSourceOptions.length > 0,
    generationInProgressElsewhere: Boolean(generationRequest.isPending)
      && !generationMatchesCurrentContext,
    hasActive: Boolean(sel && sel.active),
    hasEdits,
    hasGeneratableLanguage,
    hasPending,
    isDraft,
    langCount: langs.length,
    showPending,
  });

  const openGenerationSetup = (
    initialAudienceKeys = generationDefaultAudienceKeys,
    regenerate = false,
  ) => {
    if (generationDisabledReason) {
      message.error(generationDisabledReason);
      return;
    }
    setGenerationSetupInitialAudienceKeys(initialAudienceKeys);
    setGenerationSetupSource(regenerate ? generationRun.configSource : generationDefaultSource);
    setIsRegeneration(regenerate);
    setShowGenerationSetup(true);
  };

  const openGeneration = () => {
    if (generationDisabledReason) {
      message.error(generationDisabledReason);
      return;
    }
    if (generationMatchesCurrentContext && (
      generationRequest.isPending
      || generationRequest.isSuccessful
      || generationRequest.isFailure
    )) {
      setShowGeneration(true);
      return;
    }
    openGenerationSetup(generationDefaultAudienceKeys, false);
  };

  const onGenerateContent = (
    selectedAudienceKeys,
    dateRange,
    selectedLanguage,
    selectedObjectiveID: string | undefined,
    selectedSource: GenerationConfigSource,
  ) => {
    if (generationRequest.isPending) return;
    const sourceRecord = generationRecordForSource(selectedSource);
    if (!sourceRecord) {
      message.error('That product offering version is no longer available.');
      return;
    }
    const startDate = dateRange[0].format('YYYY-MM-DD');
    const endDate = dateRange[1].format('YYYY-MM-DD');
    const productName = productDisplayName(sourceRecord, selectedLanguage);
    // Resolved against the chosen version rather than render state, so the id
    // sent as objectiveID is always an id present in the body that is sent.
    const sourceObjectives = getGenerationObjectives(
      productOfferingObjectivesEnabled,
      whitelistOf(sourceRecord, 'objectives'),
    );
    const selectedObjective = selectedObjectiveID
      ? sourceObjectives.find((objective) => objective.generationID === selectedObjectiveID)
      : undefined;
    if (selectedObjectiveID && !selectedObjective) {
      message.error('That objective is not configured on the selected configuration.');
      return;
    }
    // A draft has no key yet; a pending version keeps the logical key its
    // approved sibling would have.
    // Built for every source so the chosen version is validated the same way;
    // only sent when it is not the published version Mystique can resolve.
    const resolvedConfig = buildGenerationProductConfig(sourceRecord, {
      country,
      key: isDraft ? '' : (sourceRecord.key || ''),
      status: sourceRecord.status || ContentConfigStatus.Unspecified,
    });
    if (resolvedConfig.body.cvps.length === 0) {
      message.error('Select at least one CVP before generating content.');
      return;
    }
    if (!resolveGenerationLanguageConfig(resolvedConfig, selectedLanguage)) {
      message.error('Add a name and product overview for this language before generating content.');
      return;
    }
    const productConfig: GenerationProductConfig | null = selectedSource === 'active'
      ? null
      : resolvedConfig;
    // No inline config means Mystique resolves the product from its ACTIVE
    // cache, so an objective UCD never minted an id for cannot be named there.
    // Fail closed rather than let the backend return "Objective ID ... was not
    // found" for an id this client fabricated.
    if (selectedObjective && !productConfig && !(selectedObjective.id || '').trim()) {
      message.error('That objective is not part of the published configuration yet.');
      return;
    }
    // Only a run the UCD endpoint can express needs its config version; a run
    // carrying an inline config or an objective stays on Mystique V2.
    if (useUcdContentGeneration && !productConfig && !selectedObjective && baseConfigVersion <= 0) {
      message.error(contentGenerationConfigLatestRequest.isPending
        ? 'Please wait for the content generation config to load.'
        : 'Content generation config is unavailable. Please refresh and try again.');
      return;
    }
    const generationVertical = resolvedConfig.body.vertical;
    const requests = buildContentConfigGenerationRequests({
      audienceKeys: selectedAudienceKeys,
      baseConfigVersion,
      country,
      endDate,
      language: selectedLanguage,
      objectiveID: selectedObjective ? selectedObjective.generationID : undefined,
      productConfig,
      productName,
      productUUID: productConfig ? productConfig.meta.key : sourceRecord.key,
      startDate,
      useUcdContentGeneration,
      vertical: generationVertical,
    });
    const whitelistedAudienceKeySet = new Set(whitelistedGenerationAudienceKeys);
    const audienceLabelsByKey: Record<string, string> = {};
    const audienceWhitelistByKey: Record<string, boolean> = {};

    selectedAudienceKeys.forEach((audienceKey) => {
      audienceLabelsByKey[audienceKey] = generationAudienceLabelsByKey[audienceKey] || audienceKey;
      audienceWhitelistByKey[audienceKey] = whitelistedAudienceKeySet.has(audienceKey);
    });

    setGenerationRun({
      audienceKeys: selectedAudienceKeys,
      audienceLabelsByKey,
      audienceWhitelistByKey,
      configSource: selectedSource,
      context: {
        country,
        endDate,
        language: selectedLanguage,
        objectiveID: selectedObjective ? selectedObjective.generationID : '',
        objectiveName: selectedObjective && selectedObjective.name ? selectedObjective.name : '',
        product: productName,
        startDate,
        vertical: generationVertical,
      },
      requests,
      selectionKey: selectedKey,
    });
    setShowGenerationSetup(false);
    setGenerationSetupSource('');
    setShowGeneration(true);
    dispatch(contentConfigGenerateMystiqueV2(requests));
  };

  const retryContentGeneration = () => {
    if (generationRequest.isPending) return;
    const missingRequests = selectMissingContentConfigGenerationRequests(
      generationRun.requests,
      generationReadyAudienceKeys,
    );
    if (missingRequests.length === 0) return;
    dispatch(contentConfigGenerateMystiqueV2(missingRequests, { preserveExisting: true }));
  };

  const regenerateSingleAudience = (audienceKey) => {
    if (generationRequest.isPending) return;
    const request = generationRun.requests
      .find((item) => item.audienceKey === audienceKey);
    if (!request) return;
    dispatch(contentConfigGenerateMystiqueV2([request], { preserveExisting: true }));
  };

  const cancelGenerationSetup = () => {
    setShowGenerationSetup(false);
    setGenerationSetupSource('');
  };


  // ---- list (table) view ----
  const rowStatus = (m) => {
    if (m.pending) return 'pending';
    if (m.active) return 'active';
    return 'draft';
  };
  const rows = useMemo(() => Object.keys(merged).map((k) => {
    const m = merged[k];
    const rec = m.active || m.pending || m.draft;
    const ciEnabledLangs = langs.filter((lang) => {
      const byLang = rec && rec.byLang && rec.byLang[lang];
      return Boolean(byLang && byLang.ciEnabled && byLang.name && byLang.name.trim() && byLang.overview && byLang.overview.trim());
    });
    return {
      key: k,
      name: productDisplayName(rec, defaultLang || 'en'),
      vertical: m.vertical,
      status: rowStatus(m),
      ciEnabledLangs,
      updatedBy: rec.updatedBy || rec.createdBy || '',
      updatedAt: rec.updatedAt,
    };
  }), [merged, defaultLang, langs]); // eslint-disable-line react-hooks/exhaustive-deps
  const visibleRows = useMemo(() => {
    let r = rows;
    if (verticalFilter) r = r.filter((x) => x.vertical === verticalFilter);
    if (listTab === 'pending') r = r.filter((x) => x.status === 'pending');
    if (listTab === 'ci-enabled') r = r.filter((x) => x.ciEnabledLangs.length > 0);
    return r;
  }, [listTab, rows, verticalFilter]);

  return (
    <div className="cc-offerings-page">
      {selectedKey ? (
        <ProductOfferingDetail
          activeLang={activeLang}
          attrOptions={attrOptions}
          audienceLabelForKey={audienceLabelForKey}
          audienceListFailed={Boolean(audienceListReq.isFailure)}
          audienceCiEnabledLangs={editorCiEnabledLangs}
          audienceLangOptions={countryLangs}
          audienceListLoaded={Boolean(audienceListReq.isSuccessful)}
          audienceListLoading={Boolean(audienceListReq.isPending)}
          audienceOptions={audienceOptions}
          audienceValidationUnavailable={audienceValidationUnavailable}
          audiencesEnabled={productOfferingAudiencesEnabled}
          objectivesEnabled={productOfferingObjectivesEnabled}
          pendingActionsDisabledReason={pendingActionsDisabledReason}
          base={base}
          busy={busy}
          canCreate={canCreate}
          canEditLocally={canEditLocally}
          canReview={canReview}
          canUpdate={canUpdate}
          country={country}
          countryDefaultLang={countryDefaultLang}
          countryName={countryName}
          cvpOptions={cvpOptions}
          defaultLang={defaultLang}
          deeplinkVal={deeplinkVal}
          discardDraft={discardDraft}
          discardLocalEdits={() => setEdits(null)}
          draftReady={draftReady}
          hasEdits={hasEdits}
          hasInvalidDeeplink={hasInvalidDeeplink}
          hasInvalidAudienceLangs={hasInvalidAudienceLangs}
          hasInvalidAudienceReasons={hasInvalidAudienceReasons}
          hasInvalidAudiences={hasInvalidAudiences}
          hasPending={hasPending}
          isDraft={isDraft}
          isMine={isMine}
          generationDisabledReason={generationDisabledReason}
          langVal={langVal}
          langs={langs}
          listVal={listVal}
          nameVal={nameVal}
          onApprove={onApprove}
          onGenerate={openGeneration}
          onEditChanges={onEditChanges}
          onRecall={onRecall}
          onReject={onReject}
          onRetryAudiences={refetchAudiences}
          onSendForApproval={onSendForApproval}
          pendingAuthor={pendingAuthor}
          pendingExpiry={pendingExpiry}
          personaOptions={personaOptions}
          productNameReady={productNameReady}
          sel={sel}
          setActiveLang={setActiveLang}
          setLang={setLang}
          setDeeplinkEdit={setDeeplinkEdit}
          setList={setList}
          setNameEdit={setNameEdit}
          setShowDiff={setShowDiff}
          setShowPending={setShowPending}
          setVerticalEdit={setVerticalEdit}
          showDiff={showDiff}
          showPending={showPending}
          verticalDeeplink={verticalDeeplink}
          verticalName={verticalName}
          verticals={verticals}
          verticalVal={verticalVal}
        />
      ) : (
        <ProductOfferingList
          country={country}
          countries={countries}
          canEditLocally={canEditLocally}
          listTab={listTab}
          setListTab={setListTab}
          loading={listPending && rows.length === 0}
          onCountryChange={onCountryChange}
          onCreate={() => {
            if (!canEditLocally) {
              notifyForbidden();
              return;
            }
            setShowNew(true);
          }}
          onOpenDetail={openDetail}
          rows={visibleRows}
          setVerticalFilter={setVerticalFilter}
          verticalFilter={verticalFilter}
          verticalName={verticalName}
          verticals={verticals}
        />
      )}

      <NewProductModal
        open={showNew}
        canEditLocally={canEditLocally}
        countries={countries}
        defaultCountry={country}
        verticals={verticals}
        onClose={() => setShowNew(false)}
        onCreate={(stub) => {
          if (!canEditLocally) {
            notifyForbidden();
            return;
          }
          // Start an in-memory draft for the chosen country and open the detail
          // view to author it; it is only persisted (CreateProduct) on "Send for
          // approval".
          setCountry(stub.country);
          setDraft(stub);
          setEditsByVersion({});
          setActiveLang('en');
          setSelectedKey(DRAFT_KEY);
          setShowNew(false);
        }}
      />

      <ContentGenerationModal
        audienceKeys={generationRun.audienceKeys}
        audienceLabelsByKey={generationRun.audienceLabelsByKey}
        audienceWhitelistByKey={generationRun.audienceWhitelistByKey}
        context={generationRun.context}
        isFailure={generationHasFailures && !generationRequest.isPending}
        isLoading={Boolean(generationRequest.isPending)}
        onClose={() => setShowGeneration(false)}
        onRegenerate={(audienceKeys) => openGenerationSetup(
          audienceKeys && audienceKeys.length > 0 ? audienceKeys : generationRun.audienceKeys,
          true,
        )}
        onRegenerateAudience={regenerateSingleAudience}
        onRetry={retryContentGeneration}
        open={showGeneration}
        readyAudienceKeys={generationReadyAudienceKeys}
        responsesByAudienceKey={generationResponsesByAudienceKey}
        configSource={generationRun.configSource}
        errorsByAudienceKey={generationErrorsByAudienceKey}
      />

      <ContentGenerationSetupModal
        audienceListFailed={Boolean(audienceListReq.isFailure)}
        audienceListLoading={Boolean(audienceListReq.isPending)}
        audiences={generationAudienceOptions}
        initialAudienceKeys={isRegeneration
          ? generationSetupInitialAudienceKeys
          : generationDefaultAudienceKeys}
        initialLanguage={isRegeneration ? generationRun.context.language : 'en'}
        initialObjectiveID={isRegeneration
          ? generationRun.context.objectiveID || generationDefaultObjectiveID
          : generationDefaultObjectiveID}
        initialSource={isRegeneration
          ? generationRun.configSource
          : generationDefaultSource}
        isRegeneration={isRegeneration}
        sources={generationSourceOptions}
        languages={generationSetupLanguages}
        objectives={generationObjectiveOptions}
        onCancel={cancelGenerationSetup}
        onRetryAudiences={refetchAudiences}
        onGenerate={onGenerateContent}
        onSourceChange={setGenerationSetupSource}
        open={showGenerationSetup}
      />
    </div>
  );
};

export default ProductOfferingPage;
